import { IconChevronDown } from "../../components/icons"
import { createContext, Fragment, useContext, useState } from "react"
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useTranslation } from "react-i18next"
import _ from "lodash"
import clsx from "clsx"

import { useAuth, useRoles } from "../../user/Auth"
import { useLocalState } from "../../settings/localStorage"
import { useCrud } from "../../BackendProvider"
import Modal from "../../components/Modal"
import { Field, Form, Formik } from "formik"
import { family_member } from "src/data"
import { useDwSettingsProvider } from "./DwSettings"

interface QueryAction {
    value: string
    label: string
    disabled?: boolean
}

export interface Query {
    queryId?: number
    entity?: string
    queryName: string
    username: string
    groupBy: string
    fields: string[]
}


const QueriesContext = createContext({
    queryActions: [] as QueryAction[],
    queryAction: "" as string,
    selectedQueryId: -1, 
    setSelectedQueryId: (id: number) => {},
    selectedQuery: undefined as Query | undefined,
    setQueryModal: (modal: QueryModal | undefined) => {},
    showSaveAsModal: false as boolean,
    showOpenModal: false as boolean,
    showDeleteModal: false as boolean,
    selectQuery: (action: string | number) => {},

    dataQueries: [] as Query[], 
    postQuery: (query: Query) => Promise.resolve(query),
    //deleteQuery: (id: number | string) => Promise.resolve({}),
    deleteQuery: (query: Query) => Promise.resolve({}),
})

type QueryModal = "save-as" | "open" | "delete"

export function QueriesProvider({entity, children}) {
    //console.log('recalculating QP')
    const {t} = useTranslation()
    const {isEditUser} = useRoles()


    const {data: _dataQueries, postMutation, deleteMutation: deleteQuery} = useCrud<Query>('data-query', (dq: Query) => dq.queryId)
    const dataQueries = _dataQueries.filter(dq => (dq.entity ?? family_member) === entity)


    async function postQuery(query: Query) {
        return postMutation({...query, entity})
    }

    const { searchFields, setSearchFields, setGroupBy, defaultGroupBy } = useDwSettingsProvider()

    const [selectedQueryId, setSelectedQueryId] = useLocalState(entity + '-analytics-query-id', -1)
    const [queryAction, setQueryAction] = useState("")

    const [queryModal, setQueryModal] = useState(undefined as QueryModal | undefined)
    const showSaveAsModal = queryModal === "save-as"
    const showOpenModal = queryModal === "open"
    const showDeleteModal = queryModal === "delete"

    const dataQueryById = _.keyBy(dataQueries, q => q.queryId)

    const selectedQuery = dataQueryById[selectedQueryId]

    const queryActions:  QueryAction[] = [
        {disabled: false, value: "_new", label: t('new')},
        {disabled: dataQueries.length === 0, value: "_open", label: t('open')},
        {disabled: !isEditUser || selectedQuery === undefined, value: "_save", label: t('save')},
        {disabled: !isEditUser || searchFields.length === 0, value: "_save_as", label: t('save-as')},
        {disabled: !isEditUser || selectedQuery === undefined, value: "_rename", label: t('rename')},
        {disabled: !isEditUser || selectedQuery === undefined, value: "_delete", label: t('delete') + " " + (selectedQuery?.queryName ?? '')},
    ]

    function selectQuery(action: string | number) {
        //const action = event?.target?.value
        switch(action) {
            case "_new":
                setSearchFields([])
                setGroupBy(defaultGroupBy)
                setSelectedQueryId(undefined)
                break
            case "_open": 
                setQueryModal("open")
                break
            case "_save": 
            case "_save_as": 
            case "_rename": 
                setQueryAction(action)
                setQueryModal("save-as")
                break
            case "_delete": 
                setQueryModal("delete")
                break
            case "": break
            default:
                const queryId = action as number
                const query = dataQueryById[queryId]
                setSelectedQueryId(queryId)
                //console.log(query)
                setGroupBy(query.groupBy)
                setSearchFields(query.fields)
        }
    }

    const value = {
        queryAction, queryActions,
        setQueryModal,
        selectQuery,
        selectedQueryId, setSelectedQueryId, selectedQuery,
        showOpenModal, showSaveAsModal, showDeleteModal,
        dataQueries, postQuery, deleteQuery,
    }

    return <QueriesContext.Provider {...{value}}>
        {children}
    </QueriesContext.Provider>
}

export function useQueriesProvider() {
    return useContext(QueriesContext)
}

export function QueryMenu() { //{selectQuery, queryActions}: {selectQuery: (query: string) => void, queryActions: QueryAction[]}) {
    const {t} = useTranslation()
    const {selectQuery, queryActions} = useQueriesProvider()
    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <MenuButton className="btn-secondary rounded-sm inline-flex justify-center font-normal focus:outline-0 focus:ring-0 whitespace-nowrap">
                    {t('query-actions')}
                    <IconChevronDown aria-hidden="true" />
                </MenuButton>
            </div>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-sm bg-white border border-pcx-500 shadow-lg ring-1 ring-pcx-600 ring-opacity-5 focus:outline-none overflow-hidden">
                    {queryActions.map(({ value, label, disabled }) =>
                            <MenuItem disabled={disabled} key={label}>
                                {({ focus }) => 
                                    <button 
                                        onClick={() => selectQuery(value)} 
                                        className={clsx(
                                            disabled ? 'text-gray-400' 
                                            : focus ? 'bg-pcx-200 text-pcx-800' : 'text-pcx-700', 
                                            'w-full text-left block px-4 py-2 text-sm')}
                                    >{label}</button>}
                            </MenuItem>
                    )}
                </MenuItems>
            </Transition>
        </Menu>
    )
}

export function QueryOpenModal() {
    const { t } = useTranslation()

    const {setSelectedQueryId, setQueryModal, dataQueries} = useQueriesProvider()
    const {setSearchFields, setGroupBy} = useDwSettingsProvider()

    const doSelect = ({queryId, groupBy, fields}: Query) => {
        setSelectedQueryId(queryId)
        setGroupBy(groupBy)
        setSearchFields(fields)
        setQueryModal(undefined)
    }

    return (
        <Modal escAction={() => setQueryModal(undefined)}>
            <div className="dark:bg-pcx-100">
                <h4 className="px-5 pt-4">{t('open-query')}</h4>
                <div className="p-4">
                    <div className="border border-pcx-300 rounded-md p-1 flex flex-col gap-1">
                        {_(dataQueries)
                            .sortBy(q => q.queryName)
                            .map(query =>
                                <button
                                    key={query.queryId}
                                    className="text-left border border-pcx-200 rounded-md px-2 py-px hover:bg-pcx-100 dark:hover:bg-pcx-200 sm:min-w-xs"
                                    onClick={() => doSelect(query)}>
                                    {query.queryName}
                                </button>)
                            .value()}
                    </div>
                </div>
                <div className="px-5 py-4 bg-pcx-200 flex flex-row-reverse gap-2">
                    <button onClick={() => setQueryModal(undefined)} className="btn-secondary">{t('cancel')}</button>
                </div>
            </div>
        </Modal>
    )
}

export function QueryDeleteModal() {
    const { t } = useTranslation()

    const {selectedQuery, setQueryModal, setSelectedQueryId, deleteQuery} = useQueriesProvider()

    const doDelete = async () => {
        await deleteQuery(selectedQuery)
        setSelectedQueryId(undefined)
        setQueryModal(undefined)
    }

    return (
        <Modal escAction={() => setQueryModal(undefined)}>
            <div>
                <h4 className="p-4">{t('delete-query-question', {queryName: selectedQuery?.queryName})}</h4>
                <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-2">
                    <button onClick={() => doDelete()} className="btn-warn">{t('delete')}</button>
                    <button onClick={() => setQueryModal(undefined)} className="btn-secondary">{t('cancel')}</button>
                </div>
            </div>
        </Modal>
    )
}

export interface QueryInput {
    queryName?: string
}

export function QueryEditorModal() {
    const { t } = useTranslation()
    const {user: username} = useAuth()

    const {groupBy, searchFields: fields} = useDwSettingsProvider()
    const {setQueryModal, selectedQueryId, setSelectedQueryId, queryAction, dataQueries, postQuery} = useQueriesProvider()

    //const selectedQueryId = +_selectedQueryId
    const selectedQuery = dataQueries.find(q => q.queryId === selectedQueryId)

    const ops = (queryAction === "_save_as" || selectedQuery === undefined) ? "add" : "update"
    const isSave = queryAction === "_save" && selectedQuery?.queryName
    const isRename = queryAction === "_rename"

    const otherQueries = new Set(dataQueries.map(q => q.queryName))

    async function operation(queryName: string) {
        const queryId = ops === "add" ? undefined : selectedQueryId
        const dataQuery = { queryName, username, groupBy, fields, queryId }
        const resp: Query = await postQuery(dataQuery)
        if (resp?.queryId)
            setSelectedQueryId(resp?.queryId)
        setQueryModal(undefined)
    }

    return (
        <Modal escAction={() => setQueryModal(undefined)}>
            {isSave
                ? <div>
                    <h3 className="p-4">{t('save-query-question')}</h3>
                    <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-2">
                        <button className="btn-primary" onClick={() => operation(selectedQuery.queryName)}>{t('save')}</button>
                        <button className="btn-secondary" onClick={() => setQueryModal(undefined)}>{t('cancel')}</button>
                    </div>
                </div>
                : <Formik
                    initialValues={{ queryName: selectedQuery?.queryName ?? '' } as QueryInput}
                    onSubmit={({ queryName }) => operation(queryName)}
                    validate={({ queryName }) => {
                        const errors = {} as QueryInput
                        if (queryName === "")
                            errors.queryName = t('empty-query-name-error')
                        else if (queryName.startsWith("_"))
                            errors.queryName = t('underscore-query-error')
                        else if (otherQueries.has(queryName))
                            errors.queryName = t('query-name-used-error')
                        return errors
                    }}
                >{({ errors, values }) =>
                    <Form>
                        <div className="p-4">
                            <h2 className="pb-2">{
                                ops === "add" 
                                    ? t('save-as-new-query')
                                    : isRename
                                    ? t("rename-query", { queryName: selectedQuery.queryName })
                                    : t("save-query", { queryName: selectedQuery.queryName })
                            }
                            </h2>
                            <label>
                                <div className="pb-1 text-slate-700">{t('query-name')}</div>
                                <Field autoFocus className="form-input w-full" name="queryName" required />
                                {errors.queryName && <div className="text-red-700 pt-2">{errors.queryName}</div>}
                            </label>
                        </div>
                        <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-2">
                            <input
                                disabled={isRename && values.queryName === selectedQuery?.queryName}
                                className="btn-primary disabled:btn-disabled"
                                type="submit" value={t('save')}
                            />
                            <button className="btn-secondary" onClick={() => setQueryModal(undefined)}>{t('cancel')}</button>
                        </div>
                    </Form>
                }</Formik>
            }
        </Modal>
    )
}